import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";

import { 
  createGating, updateGating, deleteGating, updateGatingSettings, invalidateGatingState,
  cloneGating
} from "actions/gating";

import _ from 'lodash';
import cx from 'classnames';

import {
  Popover,
  OverlayTrigger,
  Container,
  Form,
  Button
} from 'react-bootstrap';

import { HeaderContext } from 'pages/pages/components/header';
import StoryboardContext from 'components/Storyboard/context';

import { getBaseUrl } from 'services/util';

import styles from './settings.module.scss';

import { GATING_WIDGETS } from "constants/gatingWidgetsRequest";

export function GatingSettings() {

  return (
    <section className={styles.gatingSettings}>
      <GatingList />
      <GatingForm />
    </section>
  )
}

export const GatingList = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { id } = useParams();

  const storyboard = React.useContext(StoryboardContext);

  const headerContext = React.useContext(HeaderContext);
  const type = _.get(headerContext, 'type', 'page');

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const gatings = useSelector(state => _.get(state, 'gatings.items', []));
  const deletion = useSelector(state => _.get(state, 'gatings.deletion', {}));
  const creating = useSelector(state => _.get(state, 'gatings.create.creating', false));

  useEffect(() => {
    if (deletion && deletion.deleted && _.get(deletion, 'id', null) === id) {
      var nextGating = gatings[Math.max(_.get(deletion, 'deletedIndex', 0), 0)];
      if (nextGating) {
        history.push(`${baseUrl}/gating/${nextGating.id}`);
      }

      dispatch(invalidateGatingState());
    }
  }, [deletion]);

  const handleGatingClick = (gating) => {
    history.push(`${baseUrl}/gating/${gating.id}`);
  }

  const handleCreateGating = () => {

    if(!creating) {
      var createRequest = {
        name: 'Untitled Gating',
        widgets: GATING_WIDGETS,
        storyboard: storyboard.id
      }
  
      dispatch(createGating(createRequest, (response) => {
        history.push(`${baseUrl}/gating/${response.id}`);
      }));
    }
  }

  return (
    <div className={cx(styles.gatingSection, {[styles.pageSettings]: type === 'page'})}>
      <span className={styles.gatingTitle}>GATING PAGES</span>
      <div className={styles.inner}>
        {gatings.map((item, index) => {
          var selected = item.id === id;
          return <GatingItem
            key={index}
            onClick={handleGatingClick}
            gating={item}
            selected={selected}
            storyboard={storyboard}
          />
        })}
      </div>
      <span onClick={handleCreateGating} className={cx(styles.addGatingButton, {[styles.disabled] : creating})}><i className={cx('cleverstory-plus', styles.addIcon)}></i> Add Gating Form </span>
    </div>
  )
}

const GatingItem = ({ gating, selected, onClick }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const gatings = useSelector(state => _.get(state, 'gatings.items', []));

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    document.body.click();
    if (gatings.length < 1) {
      return;
    }

    dispatch(deleteGating(gating.id));
  }

  const handleCloneClick = (e) => {
    e.stopPropagation();
    document.body.click();
    
    dispatch(cloneGating(gating.id, (response) => {
      history.push(`${baseUrl}/gating/${response.id}`);
    }))
  }

  const popover = (
    <Popover bsPrefix="popover" className={styles.pageSettingsPopover}>
      <Popover.Body className={styles.pageSettingsPopoverBody}>
        <div className={styles.pageSettingsPopoverContent}>
          <span onClick={handleCloneClick}>Clone</span>
          {gatings.length > 1 && 
            <span onClick={handleDeleteClick}>Delete</span>
          }
        </div>  
      </Popover.Body>
    </Popover>
  );

  const handleOverlay = (e) => {
    e.stopPropagation();
    document.body.click();
  }

  return (
    <div className={cx(styles.gateItem, {[styles.selected]: selected})} onClick={() => onClick(gating)} >
      <label className={styles.name}>{gating.name}</label>
      <OverlayTrigger
        rootClose={true}
        trigger="click"
        placement="right"
        overlay={popover}
      >
        <i className={cx('cleverstory-more', styles.iconMore)} onClick={handleOverlay}  />
      </OverlayTrigger>
    </div>
  )
}

export const GatingForm = () => {
  const dispatch = useDispatch();

  const { id } = useParams();

  const [state, setState] = useState({
    name: ''
  });

  const gatings = useSelector(state => _.get(state, 'gatings.items', []));
  const gating = useSelector((state) => _.get(state, `gatings.gating.${id}.item`));
  const updating = useSelector(state => _.get(state, `gatings.gating.${id}.update.updating`, false));

  useEffect(() => {
    if (gating) {
      setState((state) => {
        return {
          ...state,
          name: gating.name
        }
      });
    }
  }, [gating])

  const handleNameChange = (e) => {
    setState((state) => {
      return {
        ...state,
        name: e.target.value
      }
    });
  }

  const validateForm = () => {
    let errors = {};

    const name = _.get(state, 'name') || '';

    if (name.trim() === '') {
      errors['name'] = 'Please provide valid name';
    }

    return errors;
  }

  const handleSaveChanges = () => {
    let errors = validateForm();

    setState(state => {
      return {
        ...state,
        errors: errors
      }
    });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    dispatch(updateGatingSettings(id, {
      name: state.name
    }));

    dispatch(updateGating(id))
  }

  const handleDeleteClick = () => {
    if (gatings.length < 1) {
      return;
    }

    dispatch(deleteGating(gating.id));
  }

  return (
    <div className={styles.gatingSettingForm}>
      <Container fluid>
        <div className={styles.gatingInfoWrapper}>
          <div className={styles.header}>
            <span className={styles.title}>Gating Info</span>
            {gatings.length > 1 && 
              <Button variant="info" className={styles.deleteGating} onClick={handleDeleteClick}>
                <i className={cx('cleverstory-trash', styles.iconMore)} />
              </Button>
            }
          </div>
          <div className={styles.formContainer}>
            <Form.Group>
              <Form.Label className={styles.gatingLabel}>Name</Form.Label>
              <Form.Control className={styles.gatingInput} type="text" value={state.name} onChange={handleNameChange} />
            </Form.Group>
          </div>
        </div>
        <div className={styles.actionContainer}>
          <Button variant="primary" className={styles.saveButton} onClick={handleSaveChanges}>{updating ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </Container>
    </div>
  )
}